<script lang="ts">
    import {Button, Dropdown, DropdownItem, Heading, Input, Label, Modal, Tooltip} from "flowbite-svelte";
    import {ArrowsRepeatOutline, CirclePlusSolid, ParagraphSolid, PenSolid, TrashBinSolid} from "flowbite-svelte-icons";
    import type {Contract, ContractHistory, Employee} from "$lib/types/employee";
    import {onMount} from "svelte";
    import {globalConfirm} from "$lib/stores/modal";
    import {get, put, retire} from "$lib/api";
    import {globalToast} from "$lib/stores/toast";
    import ProlongingContractModal from "$lib/partials/employee/ProlongingContractModal.svelte";
    import RetireContractModal from "$lib/partials/employee/RetireContractModal.svelte";
    import dayjs from "dayjs";
    import {asValue} from "$lib/utils/lenkradDate";
    import ReentryModal from "$lib/partials/employee/ReentryModal.svelte";
    import AdjustContractHoursModal from "$lib/partials/employee/AdjustContractHoursModal.svelte";
    import ChangeTariffModal from "$lib/partials/employee/ChangeTariffModal.svelte";
    import ChangeGroupModal from "$lib/partials/employee/ChangeGroupModal.svelte";

    export let contract: Contract
    export let currentContract: Contract

    export let employee: Employee;

    let retireModal = false;
    let reentryModal = false;
    let externalRetirement = false;
    let syncModal = false;
    let syncResult: any;
    let adjustHoursModal = false;
    let changeTariffModal = false;
    let changeGroupModal  = false;


    $: isCurrentContract = contract.id === currentContract?.id

    let isActive = false;
    $: {
       if(contract.completeTimeframe.endDate.value){
           isActive =  dayjs().isBefore(dayjs(asValue(contract.history.find((x:ContractHistory) => x.historyType === 'document' && x.hiredAs.includes('(aufgelöst)'))?.endDate || contract.currentHistory.endDate)))
       } else {
           isActive =  true
       }
    }

    let contractStati = {
        'signed': 'Unterschrieben',
        'planned': 'Geplant',
        'waiting_for_employee_signature': 'Erwarte Signatur des Mitarbeiters',
        'waiting_for_hr_signature': 'Erwarte Signatur von HR',
        'synchronized': 'Arbeitsvertrag',
    }

    let prolongingModal = false;

    const retractContract = async (silent: boolean) => {
        const unsyncedHistory = contract.history.find(x => x.employmentContractStatus !== 'synchronized')

        if(silent){
            if(unsyncedHistory && unsyncedHistory.historyType !== 'prolonging' && unsyncedHistory.endDate.stamp > contract.currentHistory.endDate.stamp){
                await put('/employee/' + employee.uuid + '/contract/' + contract.id + '/history/' + contract.currentHistory.id, {
                    ...contract.currentHistory,
                    endDate: unsyncedHistory.endDate
                })
            }
            retire(`/employee/${employee.uuid}/contract/${contract.id}/history/${unsyncedHistory?.id || contract.currentHistory.id}?silent=true`).then((emp) => {
                $globalToast.timed('Vertrag zurückgezogen', 3000)
                employee = emp
            });

        }else if(unsyncedHistory){
            $globalConfirm.confirm('Vertragsänderung verwerfen?', async () => {
                if(unsyncedHistory.historyType !== 'prolonging' && unsyncedHistory.endDate.stamp > contract.currentHistory.endDate.stamp){
                    await put('/employee/' + employee.uuid + '/contract/' + contract.id + '/history/' + contract.currentHistory.id, {
                        ...contract.currentHistory,
                        endDate: unsyncedHistory.endDate
                    })
                }
                retire(`/employee/${employee.uuid}/contract/${contract.id}/history/${unsyncedHistory.id}`).then((emp) => {
                    $globalToast.timed('Vertragsänderung zurückgezogen', 3000)
                    employee = emp
                })
            })
        } else {
            $globalConfirm.confirm('Vertrag zurückziehen und Mitarbeiter darüber informieren?', () => {
                retire(`/employee/${employee.uuid}/contract/${contract.id}`).then((emp) => {
                    $globalToast.timed('Vertrag zurückgezogen', 3000)
                    employee = emp
                })
            })
        }

    }
    const rescindHistory = async () => {
        await retire('/employee/' + employee.uuid + '/contract/' + contract.id + '/history/' + contract.currentHistory.id + '/rescind');
        window.location.reload()
    }

    let loading = false;
    const syncContract = async () => {
        loading = true;
        syncResult = await get('/hr/sync-employee/' + employee.uuid)
        loading = false;
    }

    const openRetireModal = (external: boolean) => {
        externalRetirement = external;
        retireModal = true
    }

    onMount(() => {

    })
</script>
<ChangeGroupModal bind:open={changeGroupModal} contract={currentContract ?? contract} {employee} />
<RetireContractModal bind:open={retireModal} contract={currentContract ?? contract} {employee} externally={externalRetirement} on:close={() => retireModal = false}/>
<ProlongingContractModal bind:open={prolongingModal} currentContract={currentContract ?? contract} {employee} on:close={() => prolongingModal = false}/>
<ReentryModal bind:open={reentryModal} {contract} {employee} on:close={() => reentryModal = false}/>
<AdjustContractHoursModal bind:open={adjustHoursModal} contract={contract} {employee} />
<ChangeTariffModal bind:open={changeTariffModal} currentContract={currentContract ?? contract} {employee} />
<div class="h-full flex flex-col">
    <Heading tag="h5" class="text-neutral-600 mb-3">Status: <span class="text-primary-600">{contractStati[contract.employmentContractStatus]}</span></Heading>
    {#if !contract.completeTimeframe.endDate.value || dayjs().isBefore(dayjs(contract.completeTimeframe.endDate.value ?? '')) }
        <div class="flex flex-col gap-3 flex-1">
            {#if contract.employmentContractStatus !== 'synchronized' ||
            contract.history.find(x => x.employmentContractStatus !== 'synchronized') }
                <Button on:click={() => retractContract(false)} color="red" size="sm" outline pill>
                    <TrashBinSolid class="pr-1"/>
                    Zurückziehen
                </Button>
                <Button on:click={() => retractContract(true)} color="red" size="sm" outline pill>
                    <TrashBinSolid class="pr-1"/>
                    Abbrechen (*?)
                </Button>
                <Tooltip>One E-Mail an den Mitarbeiter</Tooltip>
            {:else if contract.employmentContractStatus === 'signed'}
                <Button  on:click={() => syncModal = true} size="sm" outline pill color="light" >
                    <ArrowsRepeatOutline class="pr-1"/>
                    (re-)sync
                </Button>
            {:else}
                <Button size="sm" outline pill>
                    <PenSolid class="pr-1"/>
                    Ändern
                </Button>
                <Dropdown>
                    {#if isActive }
                        <DropdownItem on:click={() => changeTariffModal = true}>Tarif/Qualifikations-Änderung</DropdownItem>
                        <DropdownItem on:click={() => changeGroupModal = true}>Vertragsart-Änderung</DropdownItem>
                        <DropdownItem on:click={() => {adjustHoursModal = true}}>Stundenanpassung</DropdownItem>
                        {#if contract.completeTimeframe.endDate.value}
                            <DropdownItem on:click={() => prolongingModal = true}>Verlängern</DropdownItem>
                        {/if}
                    {:else if dayjs(asValue(contract.currentHistory.endDate)).isAfter(dayjs().subtract(30, 'days'))}
                        <DropdownItem on:click={() => prolongingModal = true}>Verlängern</DropdownItem>
                        <DropdownItem on:click={() => reentryModal = true}>Wiedereinstieg</DropdownItem>
                    {:else}
                        <DropdownItem on:click={() => reentryModal = true}>Wiedereinstieg</DropdownItem>
                    {/if}

                    <!--                <DropdownItem on:click={() => $globalToast.timed('Not implemented.', 5000)}>Bearbeiten</DropdownItem>-->
                </Dropdown>
                <Button size="sm" outline pill color="yellow">
                    <ParagraphSolid class="pr-1"/>
                    Auflösen
                </Button>
                <Dropdown>
                    <DropdownItem on:click={() => openRetireModal(false)}>Kündigen</DropdownItem>
                    <DropdownItem on:click={() => openRetireModal(true)}>Kündigung erhalten</DropdownItem>
                    <DropdownItem on:click={() => $globalConfirm.confirm('Sicher?', () => rescindHistory())}>Nicht angetreten</DropdownItem>
                </Dropdown>
                <Button size="sm" outline pill>
                    <CirclePlusSolid class="pr-1"/>
                    Dokument
                </Button>
                <Dropdown>
                    <DropdownItem on:click={() => $globalToast.timed('Not implemented.', 5000)}>Abmahnung</DropdownItem>
                    <DropdownItem on:click={() => $globalToast.timed('Not implemented.', 5000)}>Sondervereinbarung</DropdownItem>
                    <DropdownItem on:click={() => $globalToast.timed('Not implemented.', 5000)}>Sonstiges/Upload</DropdownItem>
                </Dropdown>
                <div class="flex-1"></div>
                <Button disabled={loading}  on:click={() => syncModal = true} size="sm" outline pill color="light" >
                    <ArrowsRepeatOutline class="pr-1"/>
                    (re-)sync
                </Button>
            {/if}

        </div>
    {:else}
        <span>Abgelaufen</span>
        <Button disabled={loading} class="mt-7" on:click={() => syncModal = true} size="sm" outline pill color="light" >
            <ArrowsRepeatOutline class="pr-1"/>
            (re-)sync
        </Button>
    {/if}


    <Modal bind:open={syncModal} title="Synchronisation">
        <p>Vertrag zu Drittanbietern übertragen?</p>
        <p class="text-primary-600">{syncResult && syncResult.result ? 'Synchronisiert' : ''}</p>
        <Button color="light" on:click={() => syncContract()}>Synchronisieren</Button>
    </Modal>

</div>